import { getElectionById } from '@/apis/elections';
import { useFeatureToggle } from '@/featureToggles/FeatureTogglesContext';
import {
  isCallingStatusFilter,
  isEngagementHistoryFilter,
  isEventAttendanceFilter,
  isTaskFilter,
  isTemplatePlaceholder,
  RosterFilter,
  SearchTreeLeafNode,
} from '@/types';
import { domainCodeAtom } from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import {
  restartKeyAtom,
  searchTemplateDataAtom,
} from '@/state/searchTemplate/searchTemplateData';
import { useQuery } from '@tanstack/react-query';
import { produce } from 'immer';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import {
  FlatSearchTree,
  isFlatSearchTreeLeafNode,
  unflattenSearchTree,
  updateFlatSearchTreeNodeFilterFieldValueInPlace,
} from '@components/LoadSearchTemplateDialog/flatSearchTree';
import { FieldMetaData } from '@components/LoadSearchTemplateDialog/templateFormControls/types';
import {
  mapElectoratesForAutocomplete,
  validateDateRangesAndUpdateMetaData,
  validateTaskAndUpdateMetaData,
} from '@components/LoadSearchTemplateDialog/util';

import { buildInitialFlatSearchTree } from './buildInitialFlatSearchTree';
import {
  checkValidEventAttendance,
  getNumEventAttendanceFilters,
} from './eventAttendanceValidation';
import { fetchRestartInitialEntities } from './fetchRestartInitialEntities';
import { debugAtom } from './stateAtoms';
import type {
  FilterFieldChangePayload,
  LoadSearchTemplateDialogProps,
  MapNodeIdToElectionElectorates,
} from './types';
import { fieldHash } from './types';

export function useLoadSearchTemplateDialog({
  searchTemplate,
  onClose,
  onCloseWithoutApply,
  fromPreview = false,
  fromRestart = false,
  previousSelectionsTree,
}: LoadSearchTemplateDialogProps) {
  const navigate = useNavigate();
  const { getAccessTokenSilently, user } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);
  const enableDevFeatures = useFeatureToggle('Developer Features');
  const [debug, setDebug] = useAtom(debugAtom);
  const [overrideValidation, setOverrideApply] = useState(false);
  const setSearchTemplateData = useSetAtom(searchTemplateDataAtom);
  const setRestartKey = useSetAtom(restartKeyAtom);
  const [electorateOverrideMap, setElectorateOverrideMap] = useState<MapNodeIdToElectionElectorates>(
    {}
  );
  const [initialEntitiesByPlaceholderKey, setInitialEntitiesByPlaceholderKey] = useState<
    Record<string, { id: number | string; label: string }[]>
  >({});

  const { initialFlatSearchTemplateTree, placeholders, initialMetaDataOfPlaceholderFields } =
    useMemo(
      () => buildInitialFlatSearchTree(searchTemplate, fromRestart, previousSelectionsTree),
      [searchTemplate, fromRestart, previousSelectionsTree]
    );

  /* eslint-disable react-you-might-not-need-an-effect/no-event-handler -- load restart entities when the dialog opens */
  useEffect(() => {
    const load = async () => {
      if (!fromRestart || !previousSelectionsTree || !domainCode) return;
      const token = await getAccessTokenSilently();
      const records = await fetchRestartInitialEntities({
        token,
        domainCode,
        userSub: user?.sub,
        placeholders,
        initialFlatSearchTemplateTree,
        getAccessTokenSilently,
      });
      setInitialEntitiesByPlaceholderKey(records);
    };
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change -- load restart entities when the dialog opens
    load();
    // Only re-fetch when node values change, not when the tree object identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fromRestart,
    previousSelectionsTree,
    placeholders,
    initialFlatSearchTemplateTree.nodes,
    getAccessTokenSilently,
    user?.sub,
    domainCode,
  ]);

  const [metaDataOfPlaceholderFields, setMetaDataOfPlaceholderFields] = useState<
    Record<string, FieldMetaData>
  >(initialMetaDataOfPlaceholderFields);

  const [flatSearchTree, setFlatSearchTree] = useState<FlatSearchTree>(initialFlatSearchTemplateTree);

  const isValidEventAttendance = checkValidEventAttendance(flatSearchTree);
  const numEventAttendance = getNumEventAttendanceFilters(flatSearchTree);

  const { data: fetchedElectorateRecords } = useQuery({
    queryKey: ['loadTemplateElectorates', placeholders, initialFlatSearchTemplateTree.nodes],
    queryFn: async () => {
      const defaultElectionElectorateRecords = {} as MapNodeIdToElectionElectorates;

      for (const { nodeId, filterNameAndField } of placeholders) {
        if (filterNameAndField === 'roster.electorateIds') {
          const filterValue = (
            (initialFlatSearchTemplateTree.nodes[nodeId] as SearchTreeLeafNode).filter as RosterFilter
          ).value;
          if (isTemplatePlaceholder(filterValue.electionId)) {
            defaultElectionElectorateRecords[nodeId] = null;
          } else {
            const token = await getAccessTokenSilently();
            const electionData = await getElectionById(token, filterValue.electionId || 0);
            const electorates = mapElectoratesForAutocomplete(electionData.electorates);
            const electorateIds = Array.isArray(filterValue.electorateIds)
              ? filterValue.electorateIds
              : [];
            const electorateValue = electorateIds.length
              ? electorates.filter((e) => electorateIds.includes(e.id))
              : [];
            defaultElectionElectorateRecords[nodeId] = {
              electionId: filterValue.electionId || 0,
              electorates,
              electorateValue,
            };
          }
        }
      }
      return defaultElectionElectorateRecords;
    },
    enabled: searchTemplate !== undefined,
    staleTime: Infinity,
  });

  const electionElectorateRecords = useMemo(
    () => ({ ...(fetchedElectorateRecords ?? {}), ...electorateOverrideMap }),
    [fetchedElectorateRecords, electorateOverrideMap]
  );

  const handleChange = async (nodeId: string, payload: FilterFieldChangePayload) => {
    const { filterNameAndField, fieldMetaData } = payload;

    const newFlatSearchTree = produce(flatSearchTree, (draft) =>
      updateFlatSearchTreeNodeFilterFieldValueInPlace(draft, nodeId, payload)
    );
    setFlatSearchTree(newFlatSearchTree);

    const newMetaDataOfPlaceholderFields = {
      ...metaDataOfPlaceholderFields,
      [fieldHash(nodeId, filterNameAndField)]: fieldMetaData,
    };

    const node = newFlatSearchTree.nodes[nodeId];

    if (isFlatSearchTreeLeafNode(node)) {
      const filter = node.filter;

      if (isCallingStatusFilter(filter)) {
        validateDateRangesAndUpdateMetaData(
          { filter, filterName: 'callingStatus' },
          newMetaDataOfPlaceholderFields,
          nodeId,
          fieldHash
        );
      } else if (isEngagementHistoryFilter(filter)) {
        validateDateRangesAndUpdateMetaData(
          { filter, filterName: 'engagementHistory' },
          newMetaDataOfPlaceholderFields,
          nodeId,
          fieldHash
        );
      } else if (isEventAttendanceFilter(filter)) {
        validateDateRangesAndUpdateMetaData(
          { filter, filterName: 'eventAttendance' },
          newMetaDataOfPlaceholderFields,
          nodeId,
          fieldHash
        );
      } else if (isTaskFilter(filter)) {
        validateTaskAndUpdateMetaData(filter, newMetaDataOfPlaceholderFields, nodeId, fieldHash);
      }
    }

    if (
      filterNameAndField === 'roster.electionId' &&
      (!electionElectorateRecords[nodeId] ||
        electionElectorateRecords[nodeId].electionId !== payload.value)
    ) {
      if (payload.value === -1) {
        setElectorateOverrideMap((prev) => ({ ...prev, [nodeId]: null }));
      } else {
        const token = await getAccessTokenSilently();
        const electionData = await getElectionById(token, payload.value || 0);
        setElectorateOverrideMap((prev) => ({
          ...prev,
          [nodeId]: {
            electionId: payload.value || 0,
            electorates: mapElectoratesForAutocomplete(electionData.electorates),
            electorateValue: [],
          },
        }));

        if (electionData.electorates.length === 0) {
          newMetaDataOfPlaceholderFields[fieldHash(nodeId, filterNameAndField)] = {
            state: 'invalid',
            error: {
              reason: 'noElectorates',
              helperText: `No roster data available for ${electionData.name}`,
            },
          };
        }
      }
    }

    setMetaDataOfPlaceholderFields(newMetaDataOfPlaceholderFields);
  };

  const allPlaceholderFieldsValid = useMemo(
    () => Object.values(metaDataOfPlaceholderFields).every(({ state }) => state === 'valid'),
    [metaDataOfPlaceholderFields]
  );

  const handleLoadSearchTemplateApply = async () => {
    const initialTree = unflattenSearchTree(flatSearchTree);

    if (fromPreview) {
      const urlPayload = {
        templateName: searchTemplate.name,
        templateId: searchTemplate.id,
        searchTreeNode: initialTree,
      };
      const urlPayloadEncoded = encodeURIComponent(JSON.stringify(urlPayload));
      window.open(`/advancedsearch?tree=${urlPayloadEncoded}`, '_blank');
      onClose();
    } else if (fromRestart) {
      setSearchTemplateData({
        templateName: searchTemplate.name,
        templateId: searchTemplate.id,
        initialTree,
        source: 'direct',
      });
      setRestartKey((k) => k + 1);
      onClose();
    } else {
      setSearchTemplateData({
        templateName: searchTemplate.name,
        templateId: searchTemplate.id,
        initialTree,
        source: 'direct',
      });
      navigate('/advancedsearch');
    }
  };

  const handleClose = () => {
    onCloseWithoutApply?.();
    onClose();
  };

  return {
    searchTemplate,
    enableDevFeatures,
    debug,
    setDebug,
    overrideValidation,
    setOverrideApply,
    placeholders,
    initialFlatSearchTemplateTree,
    metaDataOfPlaceholderFields,
    flatSearchTree,
    initialEntitiesByPlaceholderKey,
    electionElectorateRecords,
    setElectorateOverrideMap,
    handleChange,
    allPlaceholderFieldsValid,
    isValidEventAttendance,
    numEventAttendance,
    handleLoadSearchTemplateApply,
    handleClose,
  };
}
